import { useCallback } from 'react';
import { useShopify } from '../providers/AppBridgeProvider';

// Read the short-lived URL fallback when a request starts, not once when the
// module loads. App Bridge may become available after the first render.
function urlIdToken(): string | null {
  return new URLSearchParams(window.location.search).get('id_token');
}

async function getSessionToken(shopify: NonNullable<typeof window.shopify>): Promise<string | null> {
  const idTokenPromise = Promise.resolve().then(() => shopify.idToken());
  const fallbackToken = urlIdToken();

  if (fallbackToken) {
    // Race idToken() against the URL token (1.5s grace period).
    // If idToken() resolves first, great — use the fresh RS256 token.
    // If it hangs (common in production), use the URL HS256 token immediately.
    const urlTokenAfterTimeout = new Promise<string>(resolve =>
      setTimeout(() => resolve(fallbackToken), 1500)
    );
    // An immediate App Bridge rejection must also use the URL fallback.
    const token = await Promise.race([idTokenPromise.catch(() => fallbackToken), urlTokenAfterTimeout]);
    if (token === fallbackToken) {
      console.log('[Auth] Using HS256 id_token from URL (idToken() took >1.5s)');
    } else {
      console.log('[Auth] Got RS256 token via shopify.idToken()');
    }
    return token;
  }

  // No URL token available — wait for idToken() with a hard 5s timeout
  try {
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('shopify.idToken() timed out after 5s')), 5000)
    );
    const token = await Promise.race([idTokenPromise, timeoutPromise]);
    console.log('[Auth] Got RS256 token via shopify.idToken()');
    return token;
  } catch (error) {
    console.warn('[Auth] All token methods failed:', error);
    return null;
  }
}

export function useAuthenticatedFetch() {
  const { shopify, shop, isEmbedded } = useShopify();

  const authenticatedFetch = useCallback(async (
    url: string,
    options: RequestInit = {}
  ): Promise<Response> => {
    const headers = new Headers(options.headers);
    // A FormData body carries a multipart boundary the browser generates and
    // writes into Content-Type itself. Forcing application/json here would
    // strip that boundary and multer would find no file at all — the size
    // guide upload is the one caller that hits this path.
    if (!(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    if (isEmbedded) {
      // Prefer App Bridge idToken(); fall back to URL id_token; fall back to shop param
      const effectiveShopify = shopify ?? window.shopify;
      if (effectiveShopify) {
        const token = await getSessionToken(effectiveShopify);
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        } else if (shop) {
          const separator = url.includes('?') ? '&' : '?';
          url = `${url}${separator}shop=${encodeURIComponent(shop)}`;
        }
      } else {
        const fallbackToken = urlIdToken();
        if (fallbackToken) headers.set('Authorization', `Bearer ${fallbackToken}`);
      }
    } else if (shop) {
      // Non-embedded fallback (dev mode)
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}shop=${encodeURIComponent(shop)}`;
    }

    console.log('[Auth] Fetching:', url, 'has-auth:', headers.has('Authorization'));
    return fetch(url, { ...options, headers });
  }, [shopify, shop, isEmbedded]);

  return authenticatedFetch;
}
